// Display formatting. All sim times are UTC ms; spectators see company-local
// (America/Chicago) clock times so the story reads the same everywhere.
import { COMPANY_TZ } from '../../convex/shared/time';

const clockFmt = new Intl.DateTimeFormat('en-US', { timeZone: COMPANY_TZ, hour: 'numeric', minute: '2-digit' });
const clockSecFmt = new Intl.DateTimeFormat('en-US', { timeZone: COMPANY_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: COMPANY_TZ, weekday: 'short', month: 'short', day: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('en-US', { timeZone: COMPANY_TZ, weekday: 'short', hour: 'numeric', minute: '2-digit' });
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd4 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
const int = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export const fmtClock = (ms: number): string => clockFmt.format(ms);
export const fmtClockSeconds = (ms: number): string => clockSecFmt.format(ms);
export const fmtDay = (ms: number): string => dayFmt.format(ms);
export const fmtDayTime = (ms: number): string => dateTimeFmt.format(ms);
export const fmtUsd = (n: number): string => usd0.format(n);
export const fmtUsdCents = (n: number): string => usd2.format(n);
/** Tiny amounts (Decider spend) keep four decimals. */
export const fmtUsdPrecise = (n: number): string => {
  if (n === 0) return usd2.format(0);
  if (Math.abs(n) < 0.0001) return '<$0.0001';
  return Math.abs(n) < 0.01 ? usd4.format(n) : usd2.format(n);
};
export const fmtInt = (n: number): string => int.format(n);
export const fmtKmh = (n: number): string => `${Math.round(Math.max(0, n))} km/h`;
export const fmtPct = (x: number, digits = 0): string => `${(x * 100).toFixed(digits)}%`;

/** "just now", "3 min ago", "in 12 min", "2 h ago", then a clock time. `now` is passed in (no clock reads in render logic). */
export function fmtRelative(ms: number, now: number): string {
  const d = ms - now;
  const a = Math.abs(d);
  const future = d > 0;
  if (a < 45_000) return future ? 'in a moment' : 'just now';
  if (a < 60 * 60_000) {
    const m = Math.round(a / 60_000);
    return future ? `in ${m} min` : `${m} min ago`;
  }
  if (a < 12 * 60 * 60_000) {
    const h = Math.round(a / 3_600_000);
    return future ? `in ${h} h` : `${h} h ago`;
  }
  return fmtDayTime(ms);
}

export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** snake_case enum → "Sentence case". */
export function humanize(s: string): string {
  const t = s.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** First name for compact UI ("Marisol \"Mari\" Vega" → "Mari"). */
export function shortName(full: string): string {
  const nick = /"([^"]+)"/.exec(full);
  if (nick) return nick[1]!;
  return full.split(' ')[0] ?? full;
}

/** Hex "#rrggbb" → [r, g, b]. Falls back to a neutral grey. */
export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [140, 140, 150];
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
